import { Check, CircleCheck, CircleX, Info, TriangleAlert, X } from 'lucide-react';
import React from 'react';

import { classnames } from '@/utils/classnames.ts';

export interface NoticeProps extends Omit<React.ComponentPropsWithRef<'div'>, 'title'> {
    tone?: 'neutral' | 'positive' | 'negative' | 'caution';
    // What it's about, in semibold; the children follow as the text. A compact one is only its children.
    title?: React.ReactNode;
    // At the end, e.g. a button that does something about it.
    action?: React.ReactNode;
    // A line with an icon instead of a box, e.g. for the result of the button right above it.
    isCompact?: boolean;
}

const ICONS = { neutral: Info, positive: CircleCheck, negative: CircleX, caution: TriangleAlert };
const COMPACT_ICONS = { neutral: Info, positive: Check, negative: X, caution: TriangleAlert };

// Something to know about what's shown around it: a warning, why an action failed, or how it went, with an icon by its
// tone.
const Notice = ({ tone = 'neutral', title, action, isCompact = false, className, children, ...props }: NoticeProps) => {
    const toneClassName = classnames(
        tone === 'neutral' && 'text-info-text',
        tone === 'positive' && 'text-positive',
        tone === 'negative' && 'text-negative',
        tone === 'caution' && 'text-caution'
    );

    if (isCompact) {
        const Icon = COMPACT_ICONS[tone];
        return (
            <div
                {...props}
                // A line of its own, without the box's surface, so a neutral one stays muted rather than blue.
                className={classnames(
                    'flex items-center gap-2 text-sm',
                    tone === 'neutral' ? 'text-text-muted' : toneClassName,
                    className
                )}
            >
                <Icon className="size-4 shrink-0" />
                {children}
            </div>
        );
    }

    const Icon = ICONS[tone];
    return (
        <div
            {...props}
            className={classnames(
                'flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl px-4 py-3 text-sm',
                toneClassName,
                tone === 'neutral' && 'bg-info-surface',
                tone === 'positive' && 'bg-positive-surface',
                tone === 'negative' && 'bg-negative-surface',
                tone === 'caution' && 'bg-caution-surface',
                className
            )}
        >
            <div className="flex min-w-0 flex-1 basis-60 gap-2.5">
                <Icon className="mt-0.5 size-4 shrink-0" />
                <div className="flex min-w-0 flex-col gap-0.5">
                    {title && <p className="font-semibold">{title}</p>}
                    {children && <div>{children}</div>}
                </div>
            </div>
            {action}
        </div>
    );
};

export default Notice;
